/**
 * Density: ρ = m / V, unit handling, reference values and the float/sink test.
 *
 * Everything is computed in g/mL (= g/cm³, the unit most reference tables use) with mass
 * in grams and volume in millilitres; `units.ts` supplies the conversions to and from the
 * units a reader types.
 */

import { findUnit, MASS, VOLUME } from "@/lib/tool-kit/chemistry/units";

export type DensitySolve = "density" | "mass" | "volume";

export interface DensityValues {
  /** g */
  massG: number;
  /** mL */
  volumeML: number;
  /** g/mL */
  density: number;
}

const ML_PER_L = 1000;

const positive = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value) && value > 0;

/** Solve ρ = m / V for the unknown; null unless the other two are positive numbers. */
export function solveDensity(
  solve: DensitySolve,
  known: {
    massG: number | null;
    volumeML: number | null;
    density: number | null;
  }
): DensityValues | null {
  const { massG, volumeML, density } = known;
  if (solve === "density" && positive(massG) && positive(volumeML)) {
    return { massG, volumeML, density: massG / volumeML };
  }
  if (solve === "mass" && positive(density) && positive(volumeML)) {
    return { massG: density * volumeML, volumeML, density };
  }
  if (solve === "volume" && positive(massG) && positive(density)) {
    return { massG, volumeML: massG / density, density };
  }
  return null;
}

/** A density unit built from a mass unit and a volume unit of `units.ts`. */
export interface DensityUnit {
  id: string;
  symbol: string;
  /** Size of one of these in g/mL. */
  factor: number;
}

function densityUnit(
  id: string,
  symbol: string,
  massId: string,
  volumeId: string
): DensityUnit {
  const grams = findUnit(MASS, massId).factor;
  const millilitres = findUnit(VOLUME, volumeId).factor * ML_PER_L;
  return { id, symbol, factor: grams / millilitres };
}

export const DENSITY_UNITS: readonly DensityUnit[] = [
  densityUnit("gmL", "g/mL", "g", "mL"),
  densityUnit("gcm3", "g/cm³", "g", "cm3"),
  densityUnit("kgL", "kg/L", "kg", "L"),
  densityUnit("gL", "g/L", "g", "L"),
  densityUnit("mgmL", "mg/mL", "mg", "mL"),
  densityUnit("kgm3", "kg/m³", "kg", "m3"),
  densityUnit("lbft3", "lb/ft³", "lb", "ft3"),
  densityUnit("lbin3", "lb/in³", "lb", "in3"),
  densityUnit("lbgal", "lb/gal (US)", "lb", "gal"),
];

export function findDensityUnit(id: string): DensityUnit {
  return DENSITY_UNITS.find((unit) => unit.id === id) ?? DENSITY_UNITS[0];
}

export function densityToBase(value: number, unitId: string): number {
  return value * findDensityUnit(unitId).factor;
}

export function densityFromBase(value: number, unitId: string): number {
  return value / findDensityUnit(unitId).factor;
}

/**
 * Reference densities in g/mL, typical values near 20 °C unless noted.
 * Sources: CRC Handbook of Chemistry and Physics (liquids, metals, ice); dry air at
 * 20 °C and 101.325 kPa; seawater at a salinity of 35 g/kg is approximate (1.020–1.029
 * across the oceans). Real samples vary with purity, alloy and temperature.
 */
export interface ReferenceDensity {
  id: string;
  kind: "gas" | "liquid" | "solid";
  /** g/mL */
  density: number;
}

export const REFERENCE_DENSITIES: readonly ReferenceDensity[] = [
  { id: "air", kind: "gas", density: 0.001_204 },
  { id: "ethanol", kind: "liquid", density: 0.7893 },
  { id: "ice", kind: "solid", density: 0.917 },
  { id: "water", kind: "liquid", density: 0.9982 },
  { id: "seawater", kind: "liquid", density: 1.025 },
  { id: "glycerol", kind: "liquid", density: 1.261 },
  { id: "aluminum", kind: "solid", density: 2.7 },
  { id: "iron", kind: "solid", density: 7.87 },
  { id: "copper", kind: "solid", density: 8.96 },
  { id: "silver", kind: "solid", density: 10.5 },
  { id: "lead", kind: "solid", density: 11.3 },
  { id: "mercury", kind: "liquid", density: 13.55 },
  { id: "gold", kind: "solid", density: 19.3 },
];

/** Liquids offered as the fluid in the float/sink test. */
export const FLUIDS: readonly string[] = [
  "water",
  "seawater",
  "ethanol",
  "glycerol",
  "mercury",
];

export function referenceDensity(id: string): number | null {
  return REFERENCE_DENSITIES.find((r) => r.id === id)?.density ?? null;
}

/** Water at 4 °C, the reference for specific gravity (0.999 975 g/mL). */
export const WATER_4C = 0.999_975;

export interface FloatResult {
  verdict: "floats" | "sinks" | "neutral";
  /** Fraction of the object's volume below the surface when it floats (0–1). */
  submerged: number;
}

/** Archimedes: an object floats if it is less dense than the fluid it is in. */
export function floatTest(
  objectDensity: number,
  fluidDensity: number
): FloatResult | null {
  if (!(positive(objectDensity) && positive(fluidDensity))) {
    return null;
  }
  const ratio = objectDensity / fluidDensity;
  if (Math.abs(ratio - 1) < 1e-9) {
    return { verdict: "neutral", submerged: 1 };
  }
  return ratio < 1
    ? { verdict: "floats", submerged: ratio }
    : { verdict: "sinks", submerged: 1 };
}

/** The reference material within `tolerance` (a fraction) of this density, nearest first. */
export function closestMaterial(
  density: number,
  tolerance = 0.02
): ReferenceDensity | null {
  let best: ReferenceDensity | null = null;
  let bestGap = tolerance;
  for (const ref of REFERENCE_DENSITIES) {
    const gap = Math.abs(density - ref.density) / ref.density;
    if (gap <= bestGap) {
      best = ref;
      bestGap = gap;
    }
  }
  return best;
}
